import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorEl, anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe des Reiters „Durchlauf“ (`/tasks/throughput`, 08.10.2026).
 *
 * Die Seite hat zwei Zustaende hinter EINEM Umschalter („Offen“ | „Abgeschlossen“); die
 * Tabellen stehen nie gleichzeitig im DOM. `showThroughputState` klickt deshalb vor den
 * Stationen das passende Segment. Die Zaehlung („3 / 6“) bleibt dabei stabil: `openOnly`
 * merkt sich, welche Offen-Stationen es gab, solange „Offen“ zu sehen war — nach dem
 * Umschalten fehlen ihre Anker, und die Karte zaehlte sonst „2 / 2“ (Laufzeitprobe 08.10.2026).
 *
 * Kernaussagen:
 *  - **„Offen“ zeigt, wo die Schritte gerade liegen** — je Bereich bzw. Person: bereit, in
 *    Arbeit, wartet davor; „Liegt bereit seit (Ø)“ misst, wie lange Arbeit unangefasst liegt.
 *  - **Der Engpass ist eine Rechnung**: die meisten bereiten Schritte, bei Gleichstand die
 *    laengere Liegezeit, erst ab zwei Bereichen (`throughput-view.model.ts`).
 *  - **„Abgeschlossen“ misst Durchlaufzeit** vom ersten angelegten Schritt bis zum letzten
 *    erledigten — Arbeitszeit nur mit Taetigkeitszeit, der Rest ist Warten.
 */
const page = { placement: 'bottom' as const, nextCondition: 'manual' as const, showNextButton: true };

function settle(ms = 400): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Stellt den Umschalter auf „Offen“ (0) oder „Abgeschlossen“ (1). Idempotent. */
export async function showThroughputState(index: 0 | 1): Promise<void> {
  const host = anchorEl(AnchorId.TasksThroughputState);
  const options = host?.querySelectorAll<HTMLElement>('button, [role="radio"], [role="tab"]');
  const target = options?.[index];
  if (!target) return;
  const pressed =
    target.getAttribute('aria-checked') === 'true' ||
    target.getAttribute('aria-pressed') === 'true' ||
    target.getAttribute('aria-selected') === 'true';
  if (pressed) return;
  target.click();
  await settle();
}

const seenInOpen = new Map<AnchorId, boolean>();

/** `numberingSkip` fuer Stationen, deren Anker nur unter „Offen“ steht. */
function openOnly(anchor: AnchorId): () => boolean {
  return () => {
    if (!anchorMissing(AnchorId.TasksThroughputOpen)) seenInOpen.set(anchor, anchorMissing(anchor));
    return seenInOpen.get(anchor) ?? true;
  };
}

export const TASK_THROUGHPUT_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskThroughputHelp,
  version: 1,
  title: 'TASK_THROUGHPUT_HELP_TOUR_TITLE',
  description: 'TASK_THROUGHPUT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: { route: '/tasks/throughput', mode: 'auto' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 120,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_THROUGHPUT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'tp-welcome',
          anchor: AnchorId.TasksThroughputBar,
          spotlightAnchor: AnchorId.TasksThroughputBar,
          title: 'TUTORIAL.TASK_THROUGHPUT.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.WELCOME.CONTENT',
          ...page,
          showPrevButton: false,
          onBeforeShow: () => showThroughputState(0),
        },
        {
          id: 'tp-open',
          anchor: AnchorId.TasksThroughputOpen,
          spotlightAnchor: AnchorId.TasksThroughputOpen,
          title: 'TUTORIAL.TASK_THROUGHPUT.OPEN.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.OPEN.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          onBeforeShow: () => showThroughputState(0),
          skipIf: () => anchorMissing(AnchorId.TasksThroughputOpen),
          numberingSkip: openOnly(AnchorId.TasksThroughputOpen),
        },
        {
          id: 'tp-waiting',
          anchor: AnchorId.TasksThroughputWaiting,
          spotlightAnchor: AnchorId.TasksThroughputWaiting,
          title: 'TUTORIAL.TASK_THROUGHPUT.WAITING.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.WAITING.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksThroughputWaiting),
          numberingSkip: openOnly(AnchorId.TasksThroughputWaiting),
        },
        {
          id: 'tp-bottleneck',
          anchor: AnchorId.TasksThroughputBottleneck,
          spotlightAnchor: AnchorId.TasksThroughputBottleneck,
          title: 'TUTORIAL.TASK_THROUGHPUT.BOTTLENECK.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.BOTTLENECK.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksThroughputBottleneck),
          numberingSkip: openOnly(AnchorId.TasksThroughputBottleneck),
        },
        {
          id: 'tp-external',
          anchor: AnchorId.TasksThroughputExternal,
          spotlightAnchor: AnchorId.TasksThroughputExternal,
          title: 'TUTORIAL.TASK_THROUGHPUT.EXTERNAL.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.EXTERNAL.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksThroughputExternal),
          numberingSkip: openOnly(AnchorId.TasksThroughputExternal),
        },
        {
          id: 'tp-closed',
          anchor: AnchorId.TasksThroughputClosed,
          spotlightAnchor: AnchorId.TasksThroughputClosed,
          title: 'TUTORIAL.TASK_THROUGHPUT.CLOSED.TITLE',
          content: 'TUTORIAL.TASK_THROUGHPUT.CLOSED.CONTENT',
          ...page,
          optional: true,
          onBeforeShow: () => showThroughputState(1),
          // Erst NACH dem Umschalten pruefbar: der Coordinator ruft skipIf vor onBeforeShow,
          // deshalb hier nur „gibt es den Umschalter“ — fehlt die Tabelle danach (leer),
          // greift der Sichtbarkeits-Guard und die Station entfaellt still.
          skipIf: () => anchorMissing(AnchorId.TasksThroughputState),
        },
      ],
    },
  ],
};
